import type { Option } from '@byecode/ui'
import { Box, Button, Flex, IconFont, Select, Text, Tooltip } from '@byecode/ui'
import type { DataSourceAbstract, Field, FieldType, PageVariableSchema, TypeInstanceMap } from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import {
    FieldTypeTag,
    getDateVariableOption,
    getFieldOptions,
    getIsMatchInnerTypeByOperator,
    getUserDatasourceOptions,
    innerTypeJoinOperatorMap,
    useAtomData,
    VariableSelect
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'

import type { PageVariableFormSlice } from '../../../formSlice'
import * as SC from './styles'

const QUERY_DATASOURCE_RULE_OPTIONS = [
    {
        label: '所有',
        value: 'AND'
    },
    {
        label: '任一',
        value: 'OR'
    }
]

const DISABLED_FIELD_TYPE = new Set<FieldType>(['notes', 'video', 'file'])

interface ConditionConfigureProps {
    variableIndex: number
    dataSource: DataSourceAbstract
    dataSourceList: DataSourceAbstract[]
}

export const ConditionConfigure = (props: ConditionConfigureProps) => {
    const { dataSource } = props

    const { control, formState } = useFormContext<PageVariableFormSlice>()

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'filter.expression.conditions',
        rules: { required: '请至少添加一条查询条件', minLength: { value: 1, message: '请至少添加一条查询条件' } }
    })
    const { schema, viewOptions: { tableProps = [] } = {} } = dataSource || {}

    const leftFieldOptions = useMemo(() => {
        return getFieldOptions(schema, tableProps)
            .map(item => ({ ...item, extra: <FieldTypeTag type={item.type} innerType={item.innerType} /> }))
            .filter(item => !DISABLED_FIELD_TYPE.has(item.type))
    }, [schema, tableProps])

    return (
        <Box>
            <SC.FormItemHeader>
                <Flex alignItems="center" gap={4}>
                    <SC.FormLabel required>查询条件</SC.FormLabel>
                    <Tooltip title="查询满足条件的记录">
                        <IconFont type="Question" fill="var(--color-gray-400)" />
                    </Tooltip>
                </Flex>

                <Flex gap={8} alignItems="center" styles={{ root: { fontSize: 14, color: 'var(--color-gray-500)' } }}>
                    <Text color="var(--color-gray-500)">满足下方</Text>
                    <Controller
                        control={control}
                        name="filter.expression.where"
                        render={({ field }) => (
                            <Select {...field} options={QUERY_DATASOURCE_RULE_OPTIONS} size="xs" style={{ borderRadius: 100 }} />
                        )}
                    />
                    <Text color="var(--color-gray-500)">条件</Text>
                </Flex>
            </SC.FormItemHeader>

            <Flex py={8} direction="column" gap={8}>
                {fields.map((item, index) => (
                    <ConditionConfigureItem key={item.id} index={index} leftFieldOptions={leftFieldOptions} onRemove={remove} {...props} />
                ))}

                <Button
                    style={{ alignSelf: 'flex-start' }}
                    radius={100}
                    icon={<IconFont type="Add" fill="var(--color-gray-500)" />}
                    onClick={() => {
                        append(
                            {
                                idVariable: {
                                    type: VariableType.FIELD_ID,
                                    fieldIdVariable: {
                                        fieldId: leftFieldOptions[0]?.value || ''
                                    }
                                },
                                operator: '=',
                                paramList: [{ type: VariableType.VALUE }]
                            },
                            { shouldFocus: false }
                        )
                    }}
                >
                    添加
                </Button>

                {formState.errors.filter?.expression?.conditions?.root?.message && (
                    <SC.ErrorMessage>{formState.errors.filter?.expression?.conditions?.root?.message}</SC.ErrorMessage>
                )}
            </Flex>
        </Box>
    )
}

// /////////////////////////////////////// 单个条件配置项 ////////////////////////////////////////
interface ConditionConfigureItemProps extends ConditionConfigureProps {
    index: number
    leftFieldOptions: Option[]

    onRemove: (index: number) => void
}
const ConditionConfigureItem = ({
    variableIndex,
    index,
    dataSource,
    dataSourceList,
    leftFieldOptions,
    onRemove
}: ConditionConfigureItemProps) => {
    const { control, watch } = useFormContext<PageVariableFormSlice>()

    const [fieldId, operator = '='] = watch([
        `filter.expression.conditions.${index}.idVariable.fieldIdVariable.fieldId`,
        `filter.expression.conditions.${index}.operator`
    ])

    const leftField = fieldId ? dataSource.schema[fieldId] : undefined

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )

    /** *********************************** 变量选择器需要的options start ****************************** */
    const pageOption = useGetPageParameterAndVariableOptions({
        pageId,
        pageVariableFilter: useCallback((variable: PageVariableSchema, i: number) => i < variableIndex, [variableIndex])
    })
    const dateOption = useMemo(
        () => (leftField?.type === 'date' ? getDateVariableOption(['TODAY', 'TOMORROW', 'YESTERDAY']) : undefined),
        [leftField?.type]
    )

    const userOption = useGetUserRecordOptions()

    const rightInnerTypes: TypeInstanceMap[] = useMemo(() => {
        if (!leftField?.innerType) {
            return []
        }
        if (leftField.innerType === 'ARRAY' && ['contains', 'notContains'].includes(operator)) {
            return ['TEXT', 'NUMBER', 'ARRAY']
        }
        return [leftField.innerType]
    }, [leftField?.innerType, operator])
    /** *********************************** 变量选择器需要的options end ****************************** */

    const operatorOptions = useMemo(() => {
        if (!leftField) {
            return []
        }
        const { innerType } = leftField
        if (!innerType) {
            return []
        }
        return innerTypeJoinOperatorMap[innerType]
    }, [leftField])

    return (
        <Flex gap={8} alignItems="center">
            <Controller
                control={control}
                name={`filter.expression.conditions.${index}.idVariable.fieldIdVariable.fieldId`}
                render={({ field }) => <Select {...field} size="lg" options={leftFieldOptions} style={{ flex: 1 }} />}
            />
            <Controller
                control={control}
                name={`filter.expression.conditions.${index}.operator`}
                render={({ field }) => (
                    <Select {...field} size="lg" options={operatorOptions} disabled={!leftField} style={{ width: 80 }} />
                )}
            />
            {!['isEmpty', 'isNotEmpty'].includes(operator) && (
                <Controller
                    control={control}
                    name={`filter.expression.conditions.${index}.paramList.${0}`}
                    render={({ field }) => (
                        <VariableSelect
                            size="lg"
                            disabled={!leftField}
                            withinPortal
                            field={leftField}
                            innerTypes={rightInnerTypes}
                            pageOptions={pageOption}
                            userOption={userOption}
                            dateOption={dateOption}
                            width="100%"
                            disablePexels
                            popoverWidth="target"
                            style={{ flex: 1 }}
                            value={field.value}
                            onChange={field.onChange}
                        />
                    )}
                />
            )}
            <SC.DeleteButton onClick={() => onRemove(index)}>
                <IconFont type="Close" />
            </SC.DeleteButton>
        </Flex>
    )
}
